import { supabase } from '@/integrations/supabase/client';

/**
 * Minimal self-owned production error tracker.
 *
 * Captures window.onerror / unhandledrejection / error-boundary / manual
 * errors and writes them into public.error_events (authenticated sessions
 * only — public token pages are out of scope v1). A daily digest email is
 * sent by the error-digest edge function (see supabase/functions/error-digest).
 *
 * This tracker must never crash or loop the app. Any internal failure logs
 * ONE console.error and permanently disables itself for the rest of the
 * session.
 */

type ErrorSource = 'window.onerror' | 'unhandledrejection' | 'error-boundary' | 'manual';

interface CaptureContext {
  source?: ErrorSource;
  meta?: Record<string, unknown>;
}

// Messages that are noisy and not actionable — never inserted, never counted.
const IGNORED_MESSAGES = new Set([
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications.',
  'Script error.',
]);

// Flood control — module-level, resets only on page load (i.e. per session).
const MAX_PER_FINGERPRINT = 3;
const MAX_TOTAL = 25;
const perFingerprintCounts = new Map<string, number>();
let totalCount = 0;

// Dead-tracker flag — set on first internal failure, never attempt again.
let disabled = false;

/** Simple djb2 string hash, returned as a base36 string. */
function djb2(input: string): string {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 33) ^ input.charCodeAt(i);
  }
  // >>> 0 forces an unsigned 32-bit value before base36 encoding.
  return (hash >>> 0).toString(36);
}

/** First meaningful stack frame line (skips the "Name: message" header line). */
function firstStackFrame(stack?: string): string {
  if (!stack) return '';
  const lines = stack.split('\n').map((l) => l.trim()).filter(Boolean);
  // The first line is typically "Error: message" — prefer the next one (an
  // actual frame like "at foo (file.ts:1:1)") when present.
  const frame = lines.find((l) => l.startsWith('at ')) ?? lines[1] ?? lines[0] ?? '';
  // Drop the content-hashed bundle name and line:col so a redeploy does not
  // produce a "new" fingerprint for the same error.
  return frame.replace(/\/assets\/(?:[^/\s)]+\/)?([^/\s)]+)-[A-Za-z0-9_-]+\.js:\d+:\d+/g, '/assets/$1.js');
}

/** Stable fingerprint for grouping: error name + message + first stack frame. */
export function computeFingerprint(name: string, message: string, stack?: string): string {
  const key = `${name}:${message}:${firstStackFrame(stack)}`;
  return djb2(key);
}

function normalizeError(error: unknown): { name: string; message: string; stack?: string } {
  if (error instanceof Error) {
    return { name: error.name, message: error.message, stack: error.stack };
  }
  if (typeof error === 'string') {
    return { name: 'Error', message: error };
  }
  return { name: 'Error', message: String(error) };
}

/**
 * Capture an error for the tracker. Safe to call from anywhere (error
 * boundaries, manual catch blocks, window listeners) — never throws.
 */
export function captureError(error: unknown, context?: CaptureContext): void {
  if (disabled) return;

  try {
    const { name, message, stack } = normalizeError(error);

    if (IGNORED_MESSAGES.has(message)) return;

    const fingerprint = computeFingerprint(name, message, stack);

    if (totalCount >= MAX_TOTAL) return;
    const fpCount = perFingerprintCounts.get(fingerprint) ?? 0;
    if (fpCount >= MAX_PER_FINGERPRINT) return;

    // Count the ATTEMPT synchronously, before any await — otherwise a
    // synchronous burst (e.g. a render loop firing window.onerror 100x) all
    // read the same pre-increment counts and bypass both caps.
    perFingerprintCounts.set(fingerprint, fpCount + 1);
    totalCount += 1;

    const source = context?.source ?? 'manual';

    // Fire-and-forget async insert — captureError itself stays synchronous
    // and never throws regardless of what happens inside.
    void (async () => {
      try {
        const { data: sessionData } = await supabase.auth.getSession();
        if (!sessionData.session) return; // no authenticated session — skip (expected on public pages)

        const { error: insertError } = await supabase
          // error_events is not yet in the generated DB types.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          .from('error_events' as any)
          .insert({
            fingerprint,
            message,
            stack: stack ?? null,
            source,
            url: window.location.href,
            user_agent: navigator.userAgent,
            user_id: sessionData.session.user.id,
            meta: context?.meta ?? null,
          });

        if (insertError) throw new Error(insertError.message);
      } catch (e) {
        disabled = true;
        console.error('[errorTracker] disabled:', e);
      }
    })();
  } catch (e) {
    disabled = true;
    console.error('[errorTracker] disabled:', e);
  }
}

/** Registers window-level listeners. Call once at app startup (src/main.tsx). */
export function initErrorTracker(): void {
  window.addEventListener('error', (event: ErrorEvent) => {
    captureError(event.error ?? event.message, { source: 'window.onerror' });
  });

  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    captureError(event.reason, { source: 'unhandledrejection' });
  });
}
